import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Image,
  Box,
  Text,
  Spinner,
  VStack,
} from '@chakra-ui/react';
import { getOptimizedImageUrl } from '../../utils/misc';

interface ImageViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  title?: string;
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  title = 'Image'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [imageKey, setImageKey] = useState(0); // Force re-render when URL changes
  const [optimizedImageUrl, setOptimizedImageUrl] = useState('');

  // Use useRef to store scroll position more reliably
  const scrollPosition = useRef(0);

  // Optimize image URL when imageUrl changes
  useEffect(() => {
    if (imageUrl) {
      // Optimize for modal viewing with higher quality and larger size
      const optimized = getOptimizedImageUrl(imageUrl, 1200, 90);
      setOptimizedImageUrl(optimized);
    } else {
      setOptimizedImageUrl('');
    }
  }, [imageUrl]);

  // Reset states when modal opens/closes or URL changes
  useEffect(() => {
    if (isOpen && optimizedImageUrl) {
      // Store current scroll position when opening modal
      scrollPosition.current = window.pageYOffset || document.documentElement.scrollTop;
      setIsLoading(true);
      setHasError(false);
      setImageKey(prev => prev + 1); // Force image re-render
    } else if (!isOpen) {
      // Reset everything when modal closes
      setTimeout(() => {
        setIsLoading(true);
        setHasError(false);
      }, 300); // Wait for modal close animation
    }
  }, [isOpen, optimizedImageUrl]);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Enhanced close handler with scroll position preservation
  const handleClose = () => {
    setIsLoading(true); // Reset immediately for better UX
    setHasError(false);
    onClose();
    
    // Restore scroll position after modal closes with multiple fallback methods
    requestAnimationFrame(() => {
      window.scrollTo({
        top: scrollPosition.current,
        behavior: 'instant' // Instant scroll to prevent any animation
      });
      
      // Fallback in case scrollTo doesn't work immediately
      setTimeout(() => {
        if (window.pageYOffset !== scrollPosition.current) {
          document.documentElement.scrollTop = scrollPosition.current;
          document.body.scrollTop = scrollPosition.current; // For Safari
        }
      }, 50);
    });
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      size="full" 
      isCentered
      closeOnEsc={true}
      closeOnOverlayClick={true}
      preserveScrollBarGap={true}
      blockScrollOnMount={true}
      returnFocusOnClose={false} // Prevent focus return that might cause scroll
      trapFocus={false} // Prevent focus trapping that might interfere
    >
      <ModalOverlay 
        bg="blackAlpha.900" 
        backdropFilter="blur(4px)"
        zIndex={1400} // Higher z-index to prevent conflicts
      />
      <ModalContent 
        bg="black" 
        maxW="100vw" 
        maxH="100vh" 
        borderRadius="0"
        zIndex={1401} // Ensure content is above overlay
        margin={0}
      >
        <ModalHeader 
          color="white" 
          position="absolute" 
          top={0} 
          left={0} 
          right={0}
          zIndex={1402} 
          bg="rgba(0,0,0,0.7)" 
          backdropFilter="blur(8px)"
          borderBottom="1px solid rgba(255,255,255,0.1)"
          py={4}
          px={6}
        >
          <Text fontSize="lg" fontWeight="600" noOfLines={1}>
            {title}
          </Text>
        </ModalHeader>
        
        <ModalCloseButton 
          color="white" 
          fontSize="xl" 
          top={3} 
          right={4} 
          zIndex={1403}
          bg="rgba(0,0,0,0.5)"
          borderRadius="full"
          _hover={{
            bg: "rgba(255,255,255,0.2)"
          }}
        />
        
        <ModalBody 
          p={0} 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          position="relative"
          minH="100vh"
          pt="60px" // Account for header
        >
          {/* Loading Spinner - Always centered */}
          {isLoading && (
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              zIndex={1404}
            >
              <VStack spacing={4}>
                <Spinner 
                  size="xl" 
                  color="white" 
                  thickness="4px"
                  emptyColor="gray.600"
                />
                <Text color="white" fontSize="lg" fontWeight="500">
                  Loading image...
                </Text>
              </VStack>
            </Box>
          )}
          
          {/* Error State - Always centered */}
          {hasError && !isLoading && (
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              zIndex={1404}
            >
              <VStack spacing={4}>
                <Box
                  w="200px"
                  h="200px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="gray.700"
                  borderRadius="md"
                  border="2px dashed"
                  borderColor="gray.500"
                >
                  <Text color="gray.300" textAlign="center" px={4}>
                    Failed to load image
                  </Text>
                </Box>
              </VStack>
            </Box>
          )}

          {/* Main Image - Only show when loaded successfully */}
          {optimizedImageUrl && !hasError && (
            <Image
              key={imageKey} // Force re-render when URL changes
              src={optimizedImageUrl}
              alt={title}
              maxW="100%"
              maxH="calc(100vh - 60px)" // Account for header
              objectFit="contain"
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                display: isLoading ? 'none' : 'block',
                opacity: isLoading ? 0 : 1,
                transition: 'opacity 0.3s ease-in-out'
              }}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ImageViewerModal;